import type { MaybeRefOrGetter } from 'vue';

import { useStorage } from '@vueuse/core';
import { computed, toValue } from 'vue';

import type { FiscalShiftTone } from '@/entities/fiscalization';

import { useAuthStore } from '@/entities/auth';
import { fiscalShiftLastOrderStorageKey, shiftClosingReminder } from '@/entities/fiscalization';
import { toLocationZone } from '@/entities/location';
import { clock, serializeInstant } from '@shared/lib/date-time';

import type { UseFiscalShiftStatusReturn } from './use-fiscal-shift-status';

/** Подложка блока смены и точка статуса по тону. */
export const fiscalShiftToneClasses: Record<FiscalShiftTone, { card: string; dot: string }> = {
  danger: { card: 'bg-(--base-error-color)/10', dot: 'bg-(--base-error-color)' },
  neutral: { card: 'bg-(--base-popup-hover-color)', dot: 'bg-(--base-secondary-text-color)' },
  success: { card: 'bg-(--base-success-color)/10', dot: 'bg-(--base-success-color)' },
  warning: { card: 'bg-(--base-warning-color)/10', dot: 'bg-(--base-warning-color)' },
};

/** Виден только до ответа GraphQL: состояние смены больше не ждёт медленного опроса ККТ. */
const FALLBACK_PRESENTATION = {
  description: 'Состояние смены сейчас появится.',
  title: 'Загружаем смену',
  tone: 'neutral' as const,
};
/** Переоткрытие идёт после закрытого подтверждения, без модалки: ход команды виден только в плашке. */
const REOPENING_PRESENTATION = {
  description: 'Касса печатает отчёт об открытии смены.',
  title: 'Открываем смену на кассе',
  tone: 'neutral' as const,
};

/** Что показать о смене: напоминание о закрытии по графику или состояние смены. */
export function useFiscalShiftPresentation(status: UseFiscalShiftStatusReturn, locationId: MaybeRefOrGetter<string>) {
  const authStore = useAuthStore();
  const dismissedAt = useStorage<string | null>(
    computed(() => `mors:fiscal-shift-reminder-dismissed:${toValue(locationId)}`),
    null,
  );
  const lastOrderAt = useStorage<string | null>(
    computed(() => fiscalShiftLastOrderStorageKey(toValue(locationId))),
    null,
  );
  const reminder = computed(() => {
    const closesAt = status.operationalStatus.value?.location_closes_at ?? null;
    const shiftOpen = status.shiftState.value?.status === 'shift_open';

    // Без открытой смены зона не нужна: плашка видна и без выбранной локации (настройки, смена организации).
    if (!shiftOpen || closesAt === null) return null;

    return shiftClosingReminder({
      closesAt,
      dismissedAt: dismissedAt.value,
      lastOrderAt: lastOrderAt.value,
      now: clock.now(),
      shiftOpen,
      zone: toLocationZone(authStore.currentLocation?.timezone),
    });
  });
  const isReopening = computed(() => status.pendingAction.value === 'reopen');
  const visiblePresentation = computed(() =>
    isReopening.value ? REOPENING_PRESENTATION : (reminder.value ?? status.shiftState.value ?? FALLBACK_PRESENTATION),
  );

  return {
    reminder,
    visiblePresentation,
    isReopening,
    cardClasses: computed(() => fiscalShiftToneClasses[visiblePresentation.value.tone].card),
    dotClasses: computed(() => fiscalShiftToneClasses[visiblePresentation.value.tone].dot),
    dismissReminder: (): void => {
      dismissedAt.value = serializeInstant(clock.now(), 'millisecond');
    },
  };
}
